import { toast } from "react-toastify";
import useSignedDocument from "../../../hooks/useSignedDocument";
import useCompleteOnboarding from "../../../hooks/useCompleteOnboarding";
import useGetDocSignatureStatusUpdate from "../../../hooks/useGetDocumentSignatureStatusUpdate";
import { submittedAgreements } from "../../../hooks/useSubmitAgreements";
import { DocumentState } from "./useDocumentState";
import { MESSAGES } from "../constants";

interface UseHandleSignParams {
  documentState: DocumentState;
  regtankLivenessFeatureFlag: boolean | null;
  token: string;
  needToCompleteOnboarding: boolean;
  needToSubmitAgreements: boolean;
  skuIds?: string[];
}

export const useHandleSign = ({
  documentState,
  regtankLivenessFeatureFlag,
  token,
  needToCompleteOnboarding,
  needToSubmitAgreements,
  skuIds = [],
}: UseHandleSignParams) => {
  const signedDocumentMutation = useSignedDocument();
  const completeOnboardingMutation = useCompleteOnboarding();
  const documentSignatureUpdateMutation = useGetDocSignatureStatusUpdate();
  const submitAgreements = submittedAgreements({ token, skuIds });

  const handleSign = () => {
    documentState.clearUrl();

    const executeSigningFlow = () => {
      const handleSigningSuccess = (data: any) => {
        sessionStorage.removeItem("regtankSessionId");
        if (needToCompleteOnboarding) {
          completeOnboardingMutation.mutate(
            { token },
            {
              onSuccess: () => {
                toast.success(MESSAGES.SIGNING_SUCCESS);
                documentState.updateDocument(
                  data.url,
                  data.documentId,
                  data.completedAt,
                );
                documentState.incrementViewerKey();
              },
              onError: (e) => {
                toast.error((e as Error).message);
              },
            },
          );
        } else {
          toast.success(MESSAGES.SIGNING_SUCCESS);
          documentState.updateDocument(
            data.url,
            data.documentId,
            data.completedAt,
          );
          documentState.incrementViewerKey();
        }
      };

      const handleSigningError = (e: any) => {
        toast.error((e as Error).message);
      };

      if (!regtankLivenessFeatureFlag) {
        signedDocumentMutation.mutate(
          { documentId: documentState.documentId, token },
          {
            onSuccess: handleSigningSuccess,
            onError: handleSigningError,
          },
        );
      } else {
        documentSignatureUpdateMutation.mutate(
          {
            token,
            documentId: documentState.documentId,
          },
          {
            onSuccess: handleSigningSuccess,
            onError: handleSigningError,
          },
        );
      }
    };

    if (needToSubmitAgreements) {
      submitAgreements()
        .then(executeSigningFlow)
        .catch((error) => {
          toast.error(error);
        });
    } else {
      executeSigningFlow();
    }
  };

  return handleSign;
};
